import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  Input,
  message,
  Select,
  Checkbox,
} from "antd";
import { handleUpdateLevel } from "../../../api/matrix";
import { handleFetchModules } from "../../../api/modules";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useState } from "react";
import getBase64 from "../../../utils/getBase64";
import { CheckCircleOutlined, DeleteOutlined } from "@ant-design/icons";
const { Option } = Select;

export default function UpdateLevel({
  isVisible,
  setVisible,
  selectedRole,
  selectedLevel,
}: any) {
  const queryClient = useQueryClient();
  const [isSelected, setIsSelected] = useState(false);
  const [selectedFile, setSelectedFile]: any = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [form] = Form.useForm();

  const { data: modules } = useQuery({
    queryKey: ["modules"],
    queryFn: handleFetchModules,
  });

  const changeHandler = (event: any) => {
    setSelectedFile(event.target.files[0]);
    setIsSelected(true);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: handleUpdateLevel,
    onSuccess: () => {
      message.success("Updated Successfully");
      clearImage();
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => {
      message.error(`${error}`);
    },
  });

  const moduleOptions = modules?.map((model: any, index: number) => (
    <Option value={model?._id} key={index}>
      {model?.name}
    </Option>
  ));

  const onSubmit = async () => {
    const { module, description, level, procedures } =
      await form.getFieldsValue();
    let base64,
      type,
      size = null;

    if (selectedFile) {
      base64 = await getBase64(selectedFile);
      type = selectedFile?.type?.slice(selectedFile?.type - 3);
      size = selectedFile?.size;
    }
    const data = {
      module,
      description,
      procedures,
      level,
      hasMore,
      image: base64,
    };
    await mutate({ _id: selectedRole?._id, levelId: selectedLevel?._id, data });
    setVisible(false);
  };

  const clearImage = async () => {
    form.resetFields();
    form.setFieldsValue({ file: null });
    setSelectedFile(null);
    setIsSelected(false);
  };

  const clearForm = async () => {
    form.resetFields();
    form.setFieldsValue({ file: null });
    setSelectedFile(null);
    setIsSelected(false);
  };

  useEffect(() => {
    form.setFieldsValue({
      module: selectedLevel?.moduleRef,
      level: selectedLevel?.level,
      description: selectedLevel?.description,
      procedures: selectedLevel?.procedures,
    });
    setHasMore(selectedLevel?.hasMore);
  }, [selectedLevel]);

  return (
    <Modal
      open={isVisible}
      title="Update Module Level"
      onCancel={() => setVisible(false)}
      footer={[
        <Button key="back" onClick={clearForm}>
          Clear
        </Button>,
        <Button
          key="submit"
          form="updateLevel"
          htmlType="submit"
          type="primary"
          onSubmit={onSubmit}
          loading={isPending}
        >
          Submit
        </Button>,
      ]}
    >
      <Form
        id="updateLevel"
        form={form}
        name="normal_login"
        className="login-form"
        onFinish={onSubmit}
        layout="vertical"
      >
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="module"
              label={"Module"}
              rules={[
                {
                  required: true,
                  message: "Please select module",
                },
              ]}
            >
              <Select placeholder="Model">{moduleOptions}</Select>
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="level"
              label="Level"
              rules={[
                {
                  required: true,
                  message: "Please enter level",
                },
              ]}
            >
              <Input type="number" step={1} min={1} placeholder="Level" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} lg={{ span: 24 }}>
            <Form.Item
              name="description"
              label="Description"
              rules={[
                {
                  required: true,
                  message: "Please enter description",
                },
              ]}
            >
              <TextArea placeholder="Description" />
            </Form.Item>
          </Col>
        </Row>

        <div className="my-2">
          <Checkbox checked={hasMore} onChange={() => setHasMore(!hasMore)}>
            More details
          </Checkbox>
        </div>

        <div className="">
          {hasMore && (
            <div className="">
              <Row gutter={[16, 0]}>
                <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                  <Form.Item
                    name="procedures"
                    label="Procedures"
                    rules={[
                      {
                        required: true,
                        message: "Please enter procedures",
                      },
                    ]}
                  >
                    <TextArea placeholder="Procedures" />
                  </Form.Item>
                </Col>
              </Row>
              <div className="flex flex-col justify-center items-center bg-gray-100 h-32 rounded-md border">
                <div className="flex flex-col justify-center items-center">
                  {isSelected ? (
                    <div className="flex flex-col justify-center items-center">
                      <CheckCircleOutlined color={"#16a34a"} size={30} />
                      <div className="text-gray-600 mt-2">Image Selected</div>
                      <div className="text-gray-500">{selectedFile?.name}</div>
                      <button
                        className="text-red-200 flex items-center hover:text-red-500"
                        onClick={() => clearImage()}
                      >
                        <DeleteOutlined />{" "}
                        <span className="mx-1">Remove</span>
                      </button>
                    </div>
                  ) : (
                    <Form.Item
                      name="file"
                      label="Upload Image ( Optional )"
                      rules={[
                        {
                          required: false,
                          message: "Please select file",
                        },
                      ]}
                    >
                      <Input
                        type="file"
                        placeholder="Image"
                        onChange={changeHandler}
                      />
                    </Form.Item>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
}
